import {
  LucideBarChart4,
  LucideChevronDown,
  LucideMapPin,
  LucideMapPinHouse,
  LucideMapPinned,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Button, Popover } from '~/components/ui'
import type { Listing } from '~/models'
import { fmtAddress } from '~/utils'

function buildNarrprUrl({
  query,
  action,
  detailstab,
  reporttype,
}: {
  query: string
  action: string
  detailstab?: string
  reporttype?: number
}) {
  const baseUrl = 'https://www.narrpr.com/deep-link'
  const params = new URLSearchParams({
    propertymode: 'commercial',
    action: action,
    query: query,
    cbcode: 'usREsimplifi-n',
    ssocode: 'usREsimplifi-n',
    utm_source: 'REsimplifi-REsimplifi',
    utm_medium: 'deep-link-sso',
    utm_campaign: 'Property Details',
  })
  if (detailstab) {
    params.append('detailstab', detailstab)
  }
  if (reporttype) {
    params.append('reporttype', reporttype.toString())
  }
  return `${baseUrl}?${params.toString()}`
}

export default function RprLinks({ listing }: { listing: Listing }) {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const listingRecordLink = useMemo(() => {
    return buildNarrprUrl({
      query: fmtAddress(listing.property, {
        oneLine: true,
        locationVisibility: listing.locationVisibility,
      }),
      action: 'propertydetails',
      detailstab: 'summary',
    })
  }, [listing])

  const propertyReportLink = useMemo(() => {
    return buildNarrprUrl({
      query: fmtAddress(listing.property, {
        oneLine: true,
        locationVisibility: listing.locationVisibility,
      }),
      action: 'report',
      reporttype: 8,
    })
  }, [listing])

  const tradeAreaLink = useMemo(() => {
    const url = buildNarrprUrl({
      query: `${listing.property?.city}, ${listing.property?.stateAbbr}`,
      action: 'neighborhooddetails',
    })
    return url
  }, [listing])

  const tradeAreaAnalysisReportLink = useMemo(() => {
    const url = buildNarrprUrl({
      query: `${listing.property?.city}, ${listing.property?.stateAbbr}`,
      action: 'report',
      reporttype: 10,
    })
    return url
  }, [listing])

  return (
    <div>
      <Popover open={isOpen} onOpenChange={(value) => setIsOpen(value)}>
        <Popover.Trigger asChild>
          <Button variant="outline" className="pr-3">
            <img src="/rpr-logo.png" alt="RPR Logo" className="mr-2 h-6 w-6" />
            RPR
            <LucideChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </Popover.Trigger>
        <Popover.Content
          align="start"
          className="flex max-w-52 flex-col items-start gap-1 p-0">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="flex w-full justify-start gap-2 rounded-b-none">
            <a href={listingRecordLink} target="_blank" rel="noreferrer">
              <LucideMapPin className="h-4 w-4" />
              Listing Record
            </a>
          </Button>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="flex w-full justify-start gap-2 rounded-b-none">
            <a href={propertyReportLink} target="_blank" rel="noreferrer">
              <LucideMapPinned className="h-4 w-4" />
              Property Report
            </a>
          </Button>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="flex w-full justify-start gap-2 rounded-b-none">
            <a href={tradeAreaLink} target="_blank" rel="noreferrer">
              <LucideMapPinHouse className="h-4 w-4" />
              Trade Area Report
            </a>
          </Button>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="flex w-full justify-start gap-2 rounded-b-none">
            <a
              href={tradeAreaAnalysisReportLink}
              target="_blank"
              rel="noreferrer">
              <LucideBarChart4 className="h-4 w-4" />
              Trade Area Analysis Report
            </a>
          </Button>
        </Popover.Content>
      </Popover>
    </div>
  )
}
